'use client';

import { ArrowRightIcon } from 'lucide-react';
import { useMemo } from 'react';

import triviaMobileCover from '@/assets/trivia-bg--no-text-mob.jpg';
import { Button } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { ListItemsTrivia } from '@/modules/trivia/components/ListItemsTrivia';
import type { ResponseDashTriviaInit } from '@/types/dashboard';

interface StepProps {
  onNext: () => void;
  triviaData?: ResponseDashTriviaInit;
  handleTotalStats?: () => void;
}

export const InitTrivia = ({
  onNext,
  triviaData,
  handleTotalStats,
}: StepProps) => {
  const { isMobile } = useIsDesktop();

  const noTrivias = useMemo(() => {
    return !triviaData?.hayTriviaActiva;
  }, [triviaData]);

  const notStarted = useMemo(() => {
    return (
      !triviaData?.hayTriviaActiva &&
      !triviaData?.hayTriviaAnterior &&
      triviaData?.hayTriviaPendiente
    );
  }, [triviaData]);

  const title = useMemo(() => {
    if (noTrivias || notStarted) return 'TRIVIA GRAN DT';
    return `${triviaData?.trv?.nombre ?? 'TRIVIA'}: ${
      triviaData?.trv?.nombreCategoria ?? 'GRAN DT'
    }`;
  }, [noTrivias, triviaData, notStarted]);

  const notStartedMessage = (
    <span className="text-gdt-blue font-bold text-lg">
      Pronto podr&aacute;s jugar por tu primera medalla.
    </span>
  );

  const noTriviasMessage = (
    <span className="text-gdt-blue font-bold text-lg">
      No pudiste jugar la última trivia. Recordá participar en la próxima.
    </span>
  );

  const startButton = (
    <Button
      className={isMobile ? 'w-full' : ''}
      type="button"
      variant="primary"
      onClick={onNext}
    >
      EMPEZAR
    </Button>
  );

  const statsButton = triviaData?.resultadoRanking && (
    <Button
      className="mt-4 flex mx-auto font-bold no-underline items-center space-x-2 hover:text-gdt-blue"
      type="button"
      variant="text"
      onClick={handleTotalStats}
    >
      <span>Ver estadísticas totales</span>
      <ArrowRightIcon className="stroke-gdt-blue" size={22} />
    </Button>
  );

  const renderActionSection = () => (
    <div className={`flex justify-center mt-4 ${isMobile ? 'w-full' : ''}`}>
      {notStarted
        ? notStartedMessage
        : noTrivias
        ? noTriviasMessage
        : startButton}
    </div>
  );

  const renderContent = () => (
    <>
      <h2 className="text-xl font-bold">{title}</h2>
      <div className={`flex flex-col gap-y-3 ${isMobile ? '' : ''}`}>
        <ListItemsTrivia />
      </div>
      {renderActionSection()}
      {statsButton}
    </>
  );

  const renderMobileCover = () => (
    <div className="flex flex-col relative">
      <img
        alt="Trivia"
        className="w-full h-full object-cover"
        src={triviaMobileCover}
      />
      <div className="absolute flex items-center w-full justify-center h-full">
        <span className="tracking-[3px] text-white font-bold font-bebas text-[28px] leading-6">
          ¡PARTICIPÁ POR MEDALLAS <br /> TODAS LAS FECHAS!
        </span>
      </div>
    </div>
  );

  const renderMobileContent = () => (
    <div className="flex flex-col gap-y-4 m-4">
      <div className="py-4 px-5 flex flex-col gap-y-6 bg-white rounded-lg h-full">
        {renderContent()}
      </div>
    </div>
  );

  const renderDesktopContent = () => (
    <div className="flex-1 pt-9 pl-10 flex flex-col gap-y-6 bg-white rounded-r-lg h-full">
      {renderContent()}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {renderMobileCover()}
        {renderMobileContent()}
      </>
    );
  }

  return renderDesktopContent();
};

export default InitTrivia;
